import { setViewModel } from './contractCustomerSlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { validateGovNumber } from '../landing/validators';
import { InputParams } from '../../components/BtInput';
import { NavigateFooterParams, NavigateParams } from '../../components/BtNavigate';
import { CONTRACT_CUSTOMER, MY_BOOKING_SCREEN_PATH } from '../../support/routeConstants';
import { store } from '../../app/store';
import { ButtonParams } from '../../components/BtButton';
import { LoadingParams } from '../../components/BtLoading';



export function none(): ContractCustomerScreen.None {
    return {
        type: 'none',
        texts: {
            title: '',
            body: '',
            next: '',
            govNo: '',
            faq: '',
            support: '',
            booking: '',
            metaTitle: '',
            loading: '',
            contractCustomer: '',
        },
        state: {
            govNo: '',
        },
        loading: {
            title: '', navigation: {
                back: { title: '', image: 'ico_back_arrow', visible: false },
                home: { title: '', image: 'ico_home', visible: false },
                image: 'logo_assistansbolaget',
            },
        },
    }
}

export namespace ContractCustomerScreen {
    interface Texts {
        title: string
        body: string
        next: string
        govNo: string
        booking: string
        faq: string
        support: string
        metaTitle: string
        loading: string
        contractCustomer: string
    }

    export interface State {
        govNo: string
    }

    export interface None {
        type: 'none'
        texts: Texts
        state: State
        loading: LoadingParams
    }

    export interface Loading {
        type: 'loading'
        texts: Texts
        state: State
        loading: LoadingParams
    }

    export interface Ready {
        type: 'ready'
        texts: Texts
        state: State
        greeting: string
        body: string
        govNo: InputParams
        next: ButtonParams
        navigation: NavigateFooterParams[]
        image: string
        metaTitle: string
        navigationTop: NavigateParams
    }


    export interface Finished {
        type: 'finished'
        texts: Texts
        state: State
        loading: LoadingParams
    }

    export interface Failed {
        type: 'failed'
        texts: Texts
        state: State
        navigation: NavigateParams
        title: string
        body: string
        ok: ButtonParams
        errorTitle: string
    }

    export type ViewModel = None | Loading | Finished | Ready | Failed

    export function isNone(viewModel: ViewModel): viewModel is None {
        return (viewModel as None).type === 'none';
    }
    export function isLoading(viewModel: ViewModel): viewModel is Loading {
        return (viewModel as Loading).type === 'loading';
    }
    export function isReady(viewModel: ViewModel): viewModel is Ready {
        return (viewModel as Ready).type === 'ready';
    }

    export function isFinished(viewModel: ViewModel): viewModel is Finished {
        return (viewModel as Finished).type === 'finished';
    }
    export function isFailed(viewModel: ViewModel): viewModel is Failed {
        return (viewModel as Failed).type === 'failed';
    }

    function getTexts(viewModel: ViewModel): Texts {
        if (isNone(viewModel)) return viewModel.texts
        if (isLoading(viewModel)) return viewModel.texts
        if (isReady(viewModel)) return viewModel.texts
        if (isFinished(viewModel)) return viewModel.texts
        if (isFailed(viewModel)) return viewModel.texts
        return { title: '', body: '', next: '', govNo: '', booking: '', faq: '', support: '', metaTitle: '', loading: '', contractCustomer: '' }
    }


    function loading(viewModel: ViewModel): Loading {
        const t = i18n.t;
        return {
            type: 'loading',
            texts: {
                title: t('contractCustomerScreen.title'),
                body: t('contractCustomerScreen.body'),
                next: t('contractCustomerScreen.next'),
                govNo: t('contractCustomerScreen.govNo'),
                booking: t('navigation.booking'),
                faq: t('navigation.faq'),
                support: t('navigation.support'),
                metaTitle: t('contractCustomerScreen.metaTitle'),
                loading: t('loading.title'),
                contractCustomer: t('navigation.contractCustomer'),

            },
            loading: {
                title: t('loading.title'), navigation: {
                    back: { title: '', image: 'ico_back_arrow', visible: false },
                    home: { title: '', image: 'ico_home', visible: false },
                    image: 'logo_assistansbolaget',
                },
            },
            state: viewModel.state
        }
    }


    function ready(viewModel: ViewModel, state: State, errorGovNo: string | null,): Ready {
        const texts = getTexts(viewModel)
        return {
            type: 'ready',
            texts: texts,
            state: state,
            greeting: texts.title,
            body: texts.body,
            govNo: {
                id: 'govNo',
                name: 'govNo',
                title: texts.govNo,
                message: errorGovNo ? errorGovNo : '',
                disabled: false,
                value: state.govNo,
                placeholder: '',
                type: 'text'
            },
            next: { title: texts.next, image: null, visible: true },
            navigation: [{ title: texts.booking, image: 'ico_my_booking', path: MY_BOOKING_SCREEN_PATH }, { title: texts.support, image: 'ico_support', path: '' }, { title: texts.contractCustomer, image: 'ico_home', path: CONTRACT_CUSTOMER }],
            image: 'ico_customer_lg',
            metaTitle: texts.metaTitle,
            navigationTop: {
                back: { title: '', image: 'ico_back_arrow', visible: true },
                home: { title: '', image: 'ico_home', visible: true },
                image: 'logo_assistansbolaget',
            },
        }
    }


    function finished(viewModel: ViewModel, state: State): Finished {
        const texts = getTexts(viewModel)
        return {
            type: 'finished',
            texts: texts,
            state: state,
            loading: {
                title: texts.loading, navigation: {
                    back: { title: '', image: 'ico_back_arrow', visible: false },
                    home: { title: '', image: 'ico_home', visible: false },
                    image: 'logo_assistansbolaget',
                },
            },
        }
    }


    function failed(viewModel: ViewModel, state: State): Failed {
        const texts = getTexts(viewModel)
        return {
            type: 'failed',
            texts: texts,
            state: state,
            navigation: {
                back: { title: '', image: 'ico_back_arrow', visible: false },
                home: { title: '', image: 'ico_home', visible: true },
                image: 'logo_assistansbolaget',
            },
            ok: { title: texts.next, image: null, visible: true },
            body: '',
            title: '',
            errorTitle: ''
        }
    }


    export function start(viewModel: ViewModel): ((dispatch: Dispatch<AnyAction>) => void) {
        return dispatch => {
            dispatch(setViewModel(loading(viewModel)));
        };
    }

    export async function load(viewModel: ViewModel): Promise<((dispatch: Dispatch<AnyAction>) => void)> {

        const newState = { ...viewModel.state, govNo: store.getState().appState.state.createCustomerGovNo }

        return dispatch => {
            dispatch(setViewModel(ready(viewModel, newState, null)));
        };
    }


    export async function reset(): Promise<((dispatch: Dispatch<AnyAction>) => void)> {
        return async dispatch => {
            dispatch(setViewModel(none()));

        };
    }


    export async function search(viewModel: ViewModel): Promise<((dispatch: Dispatch<AnyAction>) => void)> {
        const govNoError = validateGovNumber(viewModel.state.govNo);

        if (govNoError == null) {
            return dispatch => {
                dispatch(setViewModel(finished(viewModel, viewModel.state)));
                dispatch(setAppState({
                    ...store.getState().appState.state,
                    createCustomerGovNo: viewModel.state.govNo,
                }))
            };
        } else {
            return dispatch => {
                dispatch(setViewModel(ready(viewModel, viewModel.state, govNoError)));
            };
        }
    }

    export async function updateState(viewModel: ViewModel, event: React.ChangeEvent<HTMLInputElement>): Promise<((dispatch: Dispatch<AnyAction>) => void)> {
        const target = event.target;

        const newState = { ...viewModel.state, [target.name]: target.value }

        return async dispatch => {
            dispatch(await setViewModel(ready(viewModel, newState, null)));
        };
    }

}